import type {
  DatasetAgentPreviewRow,
  DatasetAgentsPreviewResponse,
  TrainingJobRequestBody,
} from '../types/api';
import { FINE_TUNING_MODEL_NAME_MAX_LEN } from './fineTuningAgentPayload';

export interface DatasetAgentTrainingSelection {
  rowKey: string;
  agent_id?: string;
  agent_name: string;
  config_id: string;
  base_model: string;
  selected: boolean;
  isMissing: boolean;
  backend_eligible_for_training: boolean;
  eligible_for_training: boolean;
  train_examples: number;
  validation_examples: number;
  test_examples: number;
  total_examples: number;
  configs: Array<{ config_id: string; total_examples: number }>;
}

export function datasetAgentRowKey(agentName: string, configId: string): string {
  return `${agentName.trim()}\u001f${configId.trim()}`;
}

export interface DatasetAgentReactionFilter {
  reactions?: string[];
  comment: boolean;
  groundtruth: boolean;
}

/**
 * Builds the reaction/signal filter for one agent.
 * - Untouched agent (no reaction/flags in maps): all signals on.
 * - User toggled reactions: always send `reactions` (including `[]` when all off).
 * - When all reactions are explicitly off, do not default comment/groundtruth to true.
 */
export function buildAgentReactionFilter(
  reaction?: { like: boolean; dislike: boolean },
  flags?: { comment: boolean; groundtruth: boolean }
): DatasetAgentReactionFilter {
  const hasReactionOverride = reaction != null;
  const hasFlagsOverride = flags != null;

  if (!hasReactionOverride && !hasFlagsOverride) {
    return { reactions: ['like', 'dislike'], comment: true, groundtruth: true };
  }

  const reactions: string[] = [];
  if (reaction?.like) reactions.push('like');
  if (reaction?.dislike) reactions.push('dislike');
  const allReactionsOff = hasReactionOverride && reactions.length === 0;

  const filter: DatasetAgentReactionFilter = {
    comment: hasFlagsOverride ? Boolean(flags.comment) : allReactionsOff ? false : true,
    groundtruth: hasFlagsOverride ? Boolean(flags.groundtruth) : allReactionsOff ? false : true,
    reactions: hasReactionOverride ? reactions : ['like', 'dislike'],
  };

  return filter;
}

/**
 * Assembles the `filters` (global fallback) + `agent_filters` (per-agent override)
 * payload sent to POST /training/datasets/agents/preview. Only agents the user has
 * actually toggled need an explicit override; the rest fall back to the permissive
 * global filter.
 */
export function buildDatasetAgentsFilterPayload(
  agentReactions: Record<string, { like: boolean; dislike: boolean }>,
  agentFilterFlags: Record<string, { comment: boolean; groundtruth: boolean }>
): {
  filters: DatasetAgentReactionFilter;
  agent_filters: Record<string, DatasetAgentReactionFilter>;
} {
  const names = new Set<string>([
    ...Object.keys(agentReactions || {}),
    ...Object.keys(agentFilterFlags || {}),
  ]);
  const agent_filters: Record<string, DatasetAgentReactionFilter> = {};
  names.forEach((name) => {
    const key = (name || '').trim();
    if (!key) return;
    agent_filters[key] = buildAgentReactionFilter(agentReactions[name], agentFilterFlags[name]);
  });
  return {
    filters: { reactions: ['like', 'dislike'], comment: true, groundtruth: true },
    agent_filters,
  };
}

/**
 * Merges fresh counts/eligibility from a re-run preview into existing selections
 * without resetting user-chosen base models or selection toggles. Used when only
 * the reaction filters / merge switch changed (the agent set itself is unchanged).
 */
export function applyDatasetAgentPreviewToSelections(
  existing: DatasetAgentTrainingSelection[],
  preview: DatasetAgentsPreviewResponse,
  groupByBaseModel = true
): DatasetAgentTrainingSelection[] {
  const rows = Array.isArray(preview.agents) ? preview.agents : [];
  const byName = new Map<string, DatasetAgentPreviewRow>();
  rows.forEach((row) => {
    if (row && typeof row.agent_name === 'string') byName.set(row.agent_name, row);
  });

  const merged = existing.map((sel) => {
    const row = byName.get(sel.agent_name);
    if (!row) return sel;
    const backendEligible = row.eligible_for_training !== false;
    const configs = Array.isArray(row.configs)
      ? row.configs
          .map((c) => ({
            config_id: String(c.config_id || '').trim(),
            total_examples: Number(c.total_examples) || 0,
          }))
          .filter((c) => c.config_id)
      : sel.configs;
    const config_id = configs.some((c) => c.config_id === sel.config_id)
      ? sel.config_id
      : pickNewestConfigId(configs, row.config_ids);
    return {
      ...sel,
      config_id,
      configs,
      backend_eligible_for_training: backendEligible,
      eligible_for_training: backendEligible,
      selected: backendEligible ? sel.selected : false,
      train_examples: Number(row.train_examples) || 0,
      validation_examples: Number(row.validation_examples) || 0,
      test_examples: Number(row.test_examples) || 0,
      total_examples: Number(row.total_examples) || 0,
    };
  });

  return recalculateMissingAgentGroupEligibility(merged, preview.min_examples, groupByBaseModel);
}

// Eligibility counts ALL examples of the agent across configs, not just the selected config.
// Continuous training fine-tunes an agent on data from every config it appears in (the backend
// resolves all of the agent's config_ids by agent_id), so a single-config count would wrongly
// flag agents whose data is spread across old configs as "too few examples".
function getSelectionExamples(row: DatasetAgentTrainingSelection): number {
  return row.total_examples;
}

/** Picks the newest config snapshot for an agent (last in API order). */
export function pickNewestConfigId(
  configs: Array<{ config_id: string; total_examples: number }>,
  configIds?: string[]
): string {
  const orderedIds = (configIds ?? [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (orderedIds.length > 0) {
    const newestId = orderedIds[orderedIds.length - 1];
    if (configs.some((cfg) => cfg.config_id === newestId)) {
      return newestId;
    }
  }
  if (configs.length === 0) {
    return orderedIds[orderedIds.length - 1] ?? '';
  }
  return configs[configs.length - 1].config_id;
}

export function normalizeDatasetAgentPreviewRow(row: DatasetAgentPreviewRow): DatasetAgentTrainingSelection {
  const backendEligible = row.eligible_for_training !== false;
  const configs = Array.isArray(row.configs)
    ? row.configs
        .map((c) => ({
          config_id: String(c.config_id || '').trim(),
          total_examples: Number(c.total_examples) || 0,
        }))
        .filter((c) => c.config_id)
    : [];
  const config_id = pickNewestConfigId(configs, row.config_ids);

  return {
    rowKey: datasetAgentRowKey(row.agent_name, config_id),
    agent_id: row.agent_id?.trim() || undefined,
    agent_name: row.agent_name,
    config_id,
    base_model: '',
    selected: backendEligible,
    isMissing: row.present_in_current_config === false,
    backend_eligible_for_training: backendEligible,
    eligible_for_training: backendEligible,
    train_examples: Number(row.train_examples) || 0,
    validation_examples: Number(row.validation_examples) || 0,
    test_examples: Number(row.test_examples) || 0,
    total_examples: Number(row.total_examples) || 0,
    configs,
  };
}

export function recalculateMissingAgentGroupEligibility(
  selections: DatasetAgentTrainingSelection[],
  minExamples?: number,
  groupByBaseModel = true
): DatasetAgentTrainingSelection[] {
  const threshold = typeof minExamples === 'number' && minExamples > 0 ? minExamples : 0;
  const ownExamples = (row: DatasetAgentTrainingSelection) => getSelectionExamples(row);

  if (threshold <= 0) {
    return selections.map((row) => ({
      ...row,
      eligible_for_training: row.backend_eligible_for_training,
      selected: row.backend_eligible_for_training ? row.selected : false,
    }));
  }

  if (!groupByBaseModel) {
    return selections.map((row) => {
      const eligible = ownExamples(row) >= threshold;
      return {
        ...row,
        eligible_for_training: eligible,
        selected: eligible ? row.selected : false,
      };
    });
  }

  const groupTotalsByBaseModel = new Map<string, number>();
  selections.forEach((row) => {
    const baseModel = row.base_model.trim();
    if (!baseModel) return;
    groupTotalsByBaseModel.set(
      baseModel,
      (groupTotalsByBaseModel.get(baseModel) ?? 0) + ownExamples(row)
    );
  });

  return selections.map((row) => {
    const baseModel = row.base_model.trim();
    const ownTotal = ownExamples(row);
    const groupTotal = baseModel ? groupTotalsByBaseModel.get(baseModel) ?? 0 : 0;
    const eligible = ownTotal >= threshold || groupTotal >= threshold;
    // Auto-select agents that grouping just made eligible (own < threshold but
    // group sum >= threshold). Without this they stay unselected and never reach
    // the backend, so they don't appear in the preview group.
    const newlyEligibleByGroup = eligible && !row.backend_eligible_for_training;
    return {
      ...row,
      eligible_for_training: eligible,
      selected: eligible ? row.selected || newlyEligibleByGroup : false,
    };
  });
}

export function buildDatasetAgentSelectionsFromPreview(
  preview: DatasetAgentsPreviewResponse,
  defaultBaseModel: string,
  groupByBaseModel = true
): DatasetAgentTrainingSelection[] {
  const rows = Array.isArray(preview.agents) ? preview.agents : [];
  const defaultModel = defaultBaseModel.trim();
  const selections = rows.map((row) => {
    const selection = normalizeDatasetAgentPreviewRow(row);
    if (defaultModel) {
      selection.base_model = defaultModel;
    }
    if (!selection.eligible_for_training) {
      selection.selected = false;
    }
    return selection;
  });
  return recalculateMissingAgentGroupEligibility(selections, preview.min_examples, groupByBaseModel);
}

export function deriveAgentFineTuneModelName(prefix: string, agentName: string): string {
  const slug =
    agentName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'agent';
  const base = prefix.trim();
  const combined = base ? `${base}-${slug}` : slug;
  return combined.slice(0, FINE_TUNING_MODEL_NAME_MAX_LEN);
}

export function buildTrainingAgentsPayloadFromSelections(
  selections: DatasetAgentTrainingSelection[],
  options: {
    modelNamePrefix: string;
    groupByBaseModel?: boolean;
    buildFilters?: (agentName: string) => TrainingJobRequestBody['agents'][number]['filters'];
  }
): TrainingJobRequestBody['agents'] {
  const prefix = options.modelNamePrefix.trim();
  return selections
    .filter((row) => row.selected && row.eligible_for_training && row.base_model.trim())
    .map((row) => {
      const filters = options.buildFilters?.(row.agent_name);
      return {
        ...(row.agent_id ? { agent_id: row.agent_id } : {}),
        agent_name: row.agent_name,
        config_id: row.config_id,
        base_model: row.base_model.trim(),
        ...(options.groupByBaseModel ? {} : { model_name: deriveAgentFineTuneModelName(prefix, row.agent_name) }),
        is_trainable: true,
        ...(filters && Object.keys(filters).length > 0 ? { filters } : {}),
      };
    });
}

export function countSelectedEligibleDatasetAgents(selections: DatasetAgentTrainingSelection[]): number {
  return selections.filter((row) => row.selected && row.eligible_for_training && row.base_model.trim()).length;
}

export function datasetAgentSelectionsMissingBaseModel(selections: DatasetAgentTrainingSelection[]): string[] {
  return selections
    .filter((row) => row.selected && row.eligible_for_training && !row.base_model.trim())
    .map((row) => row.agent_name);
}
